import { fetchWsToken } from "@ac-expo/api";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useRef,
  useCallback,
} from "react";
import { io, Socket } from "socket.io-client";
import { useUserContext } from "../auth/user.context";
import environment from "@web-app/src/environments/environment";

type Props = {
  children: ReactNode;
};

type SocketContextType = {
  socket: Socket | null;
  isConnected: boolean;
  sendMessage: <T extends object>(
    event: string,
    data: unknown,
    callback?: (res: T) => void
  ) => void;
  subscribe: <T extends object>(
    event: string,
    callback: (data: T) => void
  ) => () => void;
};

const defaultValue = {
  socket: null,
  isConnected: false,
  sendMessage: () => {},
  subscribe: () => () => {},
};

const WebSocketContext = createContext<SocketContextType>(defaultValue);

const MAX_RETRY_ATTEMPTS = 5;
const INITIAL_RETRY_DELAY_MS = 1000;

export const useWebSocket = () => {
  return useContext(WebSocketContext);
};

export const WebSocketProvider = ({ children }: Props) => {
  const { user, isAuthenticated } = useUserContext();
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const isConnectingRef = useRef(false);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const userRef = useRef(user);
  const isAuthenticatedRef = useRef(isAuthenticated);

  useEffect(() => {
    userRef.current = user;
    isAuthenticatedRef.current = isAuthenticated;
  }, [user, isAuthenticated]);

  const cleanupSocket = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    isConnectingRef.current = false;
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const connectWebSocket = useCallback(async () => {
    const currentUser = userRef.current;

    if (!currentUser || !isAuthenticatedRef.current || isConnectingRef.current) {
      return;
    }

    if (socketRef.current?.connected) {
      return;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    isConnectingRef.current = true;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const { wsToken } = await fetchWsToken();

      if (abortController.signal.aborted) {
        return;
      }

      const socket = io(environment.API_HOST, {
        transports: ["websocket"],
        query: { connectId: currentUser.connectId },
        auth: { token: wsToken },
        withCredentials: true,
        reconnection: false,
      });
      socketRef.current = socket;

      socket.on("connect", () => {
        retryCountRef.current = 0;
        setIsConnected(true);
      });

      socket.on("disconnect", () => {
        setIsConnected(false);
        socketRef.current = null;

        if (retryCountRef.current < MAX_RETRY_ATTEMPTS) {
          const delay = INITIAL_RETRY_DELAY_MS * Math.pow(2, retryCountRef.current);
          retryCountRef.current += 1;
          retryTimeoutRef.current = setTimeout(() => {
            isConnectingRef.current = false;
            connectWebSocket();
          }, delay);
        }
      });

      socket.on("connect_error", () => {
        socketRef.current?.disconnect();
        socketRef.current = null;

        if (retryCountRef.current < MAX_RETRY_ATTEMPTS) {
          const delay = INITIAL_RETRY_DELAY_MS * Math.pow(2, retryCountRef.current);
          retryCountRef.current += 1;
          retryTimeoutRef.current = setTimeout(() => {
            isConnectingRef.current = false;
            connectWebSocket();
          }, delay);
        }
      });
    } catch (error) {
      console.error("Failed to establish WebSocket connection:", error);
      socketRef.current = null;

      if (retryCountRef.current < MAX_RETRY_ATTEMPTS) {
        const delay = INITIAL_RETRY_DELAY_MS * Math.pow(2, retryCountRef.current);
        retryCountRef.current += 1;
        retryTimeoutRef.current = setTimeout(() => {
          isConnectingRef.current = false;
          connectWebSocket();
        }, delay);
      }
    } finally {
      isConnectingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!user || !isAuthenticated) {
      cleanupSocket();
      retryCountRef.current = 0;
      return;
    }

    connectWebSocket();

    return () => {
      cleanupSocket();
      retryCountRef.current = 0;
    };
  }, [user, isAuthenticated, connectWebSocket, cleanupSocket]);

  const sendMessage = <T extends object>(
    event: string,
    data: unknown,
    callback?: (res: T) => void
  ) => {
    if (socketRef.current) {
      socketRef.current.emit(event, data, callback);
    }
  };

  const subscribe = <T extends object>(
    event: string,
    callback: (data: T) => void
  ) => {
    if (socketRef.current) {
      socketRef.current.on(event, callback);
    } else {
      console.warn(
        `Socket is not initialized. Cannot subscribe to event: ${event}`
      );
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.off(event, callback);
      }
    };
  };

  return (
    <WebSocketContext.Provider
      value={{
        socket: socketRef.current,
        isConnected,
        sendMessage,
        subscribe,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};
